import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { toast } from "sonner";
import { Loader2, ChevronDown, CheckCircle2, Circle, Lock, Clock, ExternalLink, Layers, ArrowRight, RefreshCw, AlertTriangle, X } from "lucide-react";

export default function Roadmap() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roadmap, setRoadmap] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [regen, setRegen] = useState(false); // busy flag while regenerating
  const [confirmRegen, setConfirmRegen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/roadmap");
      setRoadmap(data.roadmap);
      if (data.roadmap?.days?.length) setOpenDay(data.roadmap.current_day);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onSwitch = () => { setLoading(true); load(); };
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => window.removeEventListener("adyap:goal-switched", onSwitch);
  }, [load]);

  const regenerate = async () => {
    if (!roadmap?.goal_id) return;
    setRegen(true);
    try {
      await api.post("/roadmap/regenerate", { goal_id: roadmap.goal_id, total_days: roadmap.total_days });
      setConfirmRegen(false);
      toast.success("Fresh roadmap generated!");
      await load();
      window.dispatchEvent(new Event("adyap:goal-switched"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setRegen(false); }
  };

  if (loading) return <AppLayout><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AppLayout>;

  if (!roadmap) {
    return (
      <AppLayout>
        <div className="text-center py-24">
          <Layers className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-3xl tracking-tighter font-extrabold">No roadmap yet</h1>
          <button onClick={() => navigate("/onboarding")} className="mt-6 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors">
            Create one <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </AppLayout>
    );
  }

  const days = roadmap.days || [];

  return (
    <AppLayout>
      <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Full roadmap · {roadmap.total_days} days</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-extrabold mt-1">{roadmap.role_target}</h1>
        </div>
        <button
          data-testid="regenerate-btn"
          onClick={() => setConfirmRegen(true)}
          disabled={regen}
          className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-primary/50 hover:bg-primary/5 text-zinc-300 hover:text-white px-4 py-2 rounded-xl transition-colors disabled:opacity-60"
          title="Rebuild this roadmap from scratch"
        >
          {regen ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Regenerate roadmap
        </button>
      </div>

      {/* Phases */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="roadmap-phases">
        {(roadmap.phases || []).map((p, i) => (
          <div key={i} className="glass rounded-2xl p-5">
            <span className="text-xs tracking-wider uppercase font-semibold text-primary">Days {p.days_range}</span>
            <h3 className="font-heading text-lg font-semibold mt-1">{p.phase_name}</h3>
            <p className="text-sm text-zinc-400 mt-1">{p.focus_area}</p>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-xl font-semibold mb-4">Daily plan</h2>
      <div className="space-y-3" data-testid="roadmap-days">
        {days.map((d) => {
          const locked = d.day_number > roadmap.current_day;
          const allDone = d.tasks.length && d.tasks.every((t) => t.completed);
          const isOpen = openDay === d.day_number;
          return (
            <div key={d.day_number} className="glass rounded-2xl overflow-hidden">
              <button data-testid={`day-header-${d.day_number}`} onClick={() => setOpenDay(isOpen ? null : d.day_number)}
                className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-3 text-left">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold shrink-0 ${allDone ? "bg-emerald-500/20 text-emerald-400" : locked ? "bg-white/5 text-zinc-500" : "bg-primary/20 text-primary"}`}>
                    {locked ? <Lock className="w-4 h-4" /> : allDone ? <CheckCircle2 className="w-4 h-4" /> : d.day_number}
                  </div>
                  <div>
                    <div className="font-medium">Day {d.day_number}</div>
                    <div className="text-sm text-zinc-400">{d.theme}</div>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-5 pb-5 space-y-3 border-t border-white/5 pt-4">
                  {d.tasks.map((t, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      {t.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" /> : <Circle className="w-4 h-4 text-zinc-600 mt-1 shrink-0" />}
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${t.completed ? "line-through text-zinc-500" : ""}`}>{t.title}</div>
                        <p className="text-sm text-zinc-400 mt-0.5">{t.description}</p>
                        <div className="flex items-center gap-3 flex-wrap mt-2">
                          <span className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" />{t.time_estimate_mins} min</span>
                          {(t.resources || []).map((r, ri) => {
                            const href = r.url || (r.search_term ? `https://www.google.com/search?q=${encodeURIComponent(r.search_term)}+${encodeURIComponent(r.platform)}` : null);
                            const label = r.title || `${r.platform}${r.search_term ? `: ${r.search_term}` : ''}`;
                            return href ? (
                              <a key={ri} href={href} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" />{label}</a>
                            ) : null;
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!d.tasks.length && <p className="text-sm text-zinc-500">Detailed tasks for this day unlock as you progress.</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmRegen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !regen && setConfirmRegen(false)}>
          <div
            className="w-full sm:max-w-md rounded-2xl p-6 border border-white/10"
            style={{ background: "rgba(15,15,15,0.98)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="regenerate-confirm-modal"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-extrabold tracking-tighter">Rebuild this roadmap?</h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    Your current <span className="text-white font-medium">{roadmap.total_days}-day</span> plan and <span className="text-white font-medium">all task progress</span> for this goal will be replaced with a fresh AI-generated plan. This can&apos;t be undone.
                  </p>
                </div>
              </div>
              <button onClick={() => !regen && setConfirmRegen(false)} className="text-zinc-500 hover:text-white" aria-label="Cancel">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmRegen(false)} disabled={regen} className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors">Cancel</button>
              <button
                data-testid="confirm-regenerate-btn"
                onClick={regenerate}
                disabled={regen}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black px-5 py-2.5 rounded-xl font-medium disabled:opacity-60 transition-colors"
              >
                {regen ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Yes, regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
